import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Handshake } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/motion";
import { getSponsors } from "@/lib/api";
import { IMAGES } from "@/data/demo";

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    getSponsors().then(setSponsors);
  }, []);

  return (
    <div data-testid="sponsors-page">
      <PageHero
        testId="sponsors-hero"
        contentKey="sponsors"
        image={IMAGES.pitch}
        overline="Ils nous font confiance"
        titleLines={[
          <span key="a" className="text-white">NOS SPONSORS</span>,
          <span key="b" className="text-gold">& PARTENAIRES</span>,
        ]}
        subtitle="Des partenaires qui partagent nos valeurs d'exigence et de performance, et qui accompagnent le développement de 3M Match Pro."
      />

      <section data-testid="sponsors-grid-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <Reveal>
          <Overline>Le mur des partenaires</Overline>
        </Reveal>

        {sponsors.length > 0 ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-noir-border border border-noir-border">
            {sponsors.map((sponsor, i) => (
              <Reveal key={sponsor.id} delay={i * 0.06} y={24}>
                {sponsor.website_url ? (
                  <a
                    href={sponsor.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`sponsor-${i}`}
                    className="group bg-white hover:bg-white/90 transition-colors duration-300 h-40 md:h-48 flex items-center justify-center px-8"
                  >
                    <SponsorContent sponsor={sponsor} />
                  </a>
                ) : (
                  <div
                    data-testid={`sponsor-${i}`}
                    className="group bg-white hover:bg-white/90 transition-colors duration-300 h-40 md:h-48 flex items-center justify-center px-8"
                  >
                    <SponsorContent sponsor={sponsor} />
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        ) : (
          <p data-testid="sponsors-empty" className="mt-14 text-white/45 text-sm">
            Nos partenaires seront dévoilés très prochainement.
          </p>
        )}
      </section>

      {/* CTA PARTENARIAT */}
      <section data-testid="sponsors-cta" className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
        <Reveal>
          <div className="relative border border-gold/25 p-10 md:p-16 overflow-hidden">
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(ellipse 50% 60% at 15% 30%, rgba(212,175,55,0.1), transparent)" }}
            />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="flex items-start gap-5">
                <Handshake size={36} className="text-gold shrink-0 mt-1" />
                <div>
                  <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight">
                    Devenir <span className="text-gold">partenaire</span>
                  </h2>
                  <p className="mt-3 text-white/60 max-w-md">
                    Associez votre image à une structure football premium et gagnez en visibilité sur
                    tous nos événements.
                  </p>
                </div>
              </div>
              <Link
                to="/contact?objet=Partenariat%20%2F%20Sponsoring"
                data-testid="sponsors-cta-contact"
                className="shrink-0 px-8 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_20px_rgba(230,0,0,0.3)] inline-flex items-center gap-3"
              >
                Discutons-en <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

const SponsorContent = ({ sponsor }) =>
  sponsor.logo_url ? (
    <img src={sponsor.logo_url} alt={sponsor.name} loading="lazy" className="max-h-16 object-contain group-hover:scale-105 transition-transform duration-500" />
  ) : (
    <span className="font-display text-2xl md:text-3xl tracking-[0.2em] uppercase text-noir/60 group-hover:text-noir transition-colors duration-300 text-center">
      {sponsor.name}
    </span>
  );

export default Sponsors;
